// Service-worker registration and silent updates.
//
// A new deploy is a new sw.js. The browser compares it against the installed
// one on every navigation (sw.js is served no-cache) and whenever we call
// registration.update(); a changed script installs in the background and then
// WAITS, because the worker is built in 'prompt' mode (vite.config.ts) and only
// activates when this page posts SKIP_WAITING. That keeps a long-lived page
// safe: until we activate the new worker, the old precache keeps serving the
// old lazy chunks, so nothing 404s mid-session.
//
// We never ask. The new version is applied the moment it's safe:
//   - the page is FRESH: just loaded, or just became visible again (on iOS
//     that is how the app "opens" — the PWA was suspended, not closed);
//   - or the page is HIDDEN, so the reload happens in the background and the
//     tab is already current when the user comes back;
//   - never while a composer is open (acquirePwaUpdateHold): a reload would
//     drop up to 800ms of typing (the draft autosave debounce) plus whatever
//     save was in flight, and close the compose window.
// A version found mid-session on a visible page simply waits for one of those.
//
// The reload is driven by `controllerchange` — the only proof a new worker
// actually took over — so a wedged activation can never reload-loop.

const SW_URL = '/sw.js';
const SW_SCOPE = '/';

// Long-lived desktop sessions never navigate, so a periodic update() is the
// only way they learn about a deploy. Runs hidden too: that's the ideal case
// (found → applied → reloaded, all in the background).
const CHECK_INTERVAL_MS = 60 * 60 * 1000;
// Coalesce the checks that quick tab flips would otherwise trigger.
const CHECK_MIN_GAP_MS = 60 * 1000;
// How long after a load / resume a found update is still applied immediately.
const FRESH_WINDOW_MS = 30 * 1000;
// skipWaiting can't finish while the outgoing worker still has in-flight
// fetch events (the boot burst of API calls); give up after this and try again
// at the next trigger rather than reloading onto the old worker.
const TAKEOVER_TIMEOUT_MS = 10 * 1000;

// On-device record of update decisions, newest last, shown in Settings →
// General → App updates. iOS PWAs have no reachable console, so this is the
// only way to see why a launch reloaded.
const DIAG_KEY = 'pwa-update-log';
const DIAG_MAX = 60;

export interface PwaLogEntry {
  at: string;
  event: string;
  detail?: Record<string, unknown>;
}

export function readPwaUpdateLog(): PwaLogEntry[] {
  try {
    return JSON.parse(localStorage.getItem(DIAG_KEY) ?? '[]') as PwaLogEntry[];
  } catch {
    return [];
  }
}

export function clearPwaUpdateLog() {
  try {
    localStorage.removeItem(DIAG_KEY);
  } catch {
    // storage unavailable
  }
}

function log(event: string, detail?: Record<string, unknown>) {
  try {
    const entries = readPwaUpdateLog();
    entries.push({ at: new Date().toISOString(), event, detail });
    localStorage.setItem(DIAG_KEY, JSON.stringify(entries.slice(-DIAG_MAX)));
  } catch {
    // storage unavailable
  }
}

/** The hashed entry chunk identifies which build this page is running. */
function pageBuild(): string {
  const src = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]')?.src ?? '';
  return src.split('/').pop() ?? '?';
}

function regState(reg: ServiceWorkerRegistration) {
  return {
    active: reg.active?.state ?? null,
    waiting: reg.waiting?.state ?? null,
    installing: reg.installing?.state ?? null,
  };
}

let initialized = false;
let registration: ServiceWorkerRegistration | null = null;
// The installed-but-not-yet-active new version, if any.
let waitingWorker: ServiceWorker | null = null;
let freshUntil = 0;
let lastCheckAt = 0;
let holds = 0;
let applying = false;
// A new worker took control (this page's apply, or another tab's) while a
// hold was active; reload as soon as the hold clears.
let reloadPending = false;

export function initPwaUpdates() {
  // Module-level guard: App's effect runs twice under StrictMode.
  if (initialized) {
    return;
  }
  initialized = true;
  if (!('serviceWorker' in navigator)) {
    return;
  }
  void init();
}

/**
 * Keep a silent update from reloading the page while the returned release
 * function hasn't been called. Used by the composers for as long as they're
 * open. Counted, so overlapping holds (dialog + inline reply, StrictMode's
 * double effect) compose correctly.
 */
export function acquirePwaUpdateHold(): () => void {
  holds += 1;
  let released = false;
  return () => {
    if (released) {
      return;
    }
    released = true;
    holds -= 1;
    if (holds > 0) {
      return;
    }
    if (reloadPending) {
      requestReload();
      return;
    }
    maybeApply();
  };
}

async function init() {
  const hadController = Boolean(navigator.serviceWorker.controller);
  const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  log('boot', {
    build: pageBuild(),
    hadController,
    navType: nav?.type ?? null,
    visibility: document.visibilityState,
  });
  freshUntil = Date.now() + FRESH_WINDOW_MS;
  // register() is itself an update check.
  lastCheckAt = Date.now();

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    log('controllerchange', {
      hadController,
      applying,
      reg: registration ? regState(registration) : null,
    });
    // A worker other than the one that served this page is now in control.
    // First-ever install claiming the page (clientsClaim) is not an update.
    if (!hadController && !applying) {
      return;
    }
    // The code this page is running no longer matches what the worker serves
    // (its precache is the NEW build's), so reload whether the activation was
    // ours or another tab's.
    applying = false;
    waitingWorker = null;
    requestReload();
  });

  try {
    registration = await navigator.serviceWorker.register(SW_URL, { scope: SW_SCOPE });
  } catch {
    // Registration failure (private mode quirks, a bad deploy): the app runs
    // straight from the network, which is fine.
    return;
  }
  const reg = registration;
  log('registered', regState(reg));

  // Already waiting: a deploy a previous session never activated. iOS suspends
  // PWAs instead of closing them, so "activate when the last client goes away"
  // never runs there and the worker stays parked across launches.
  if (reg.waiting) {
    track(reg.waiting);
  }
  // Already installing: the browser's own check on this navigation found the
  // new deploy before our code ran, so its `updatefound` fired unheard. This
  // is the normal "open the app after a deploy" case.
  if (reg.installing) {
    track(reg.installing);
  }
  reg.addEventListener('updatefound', () => {
    log('updatefound', regState(reg));
    if (reg.installing) {
      track(reg.installing);
    }
  });

  document.addEventListener('visibilitychange', onVisibilityChange);
  window.setInterval(() => {
    void checkForUpdate();
  }, CHECK_INTERVAL_MS);
}

/** Follow a new worker from install to waiting (or to redundant). */
function track(worker: ServiceWorker) {
  const onState = () => {
    log('worker-state', { state: worker.state });
    if (worker.state === 'installed') {
      onInstalled(worker);
    } else if (worker.state === 'redundant') {
      // Superseded by an even newer deploy before it activated, or the
      // install failed; the replacement gets its own `updatefound`.
      if (waitingWorker === worker) {
        waitingWorker = null;
      }
      worker.removeEventListener('statechange', onState);
    }
  };
  worker.addEventListener('statechange', onState);
  if (worker.state === 'installed') {
    onInstalled(worker);
  }
}

function onInstalled(worker: ServiceWorker) {
  // No controller means nothing served this page from a cache: either the
  // very first install (the worker activates on its own) or a hard reload,
  // where the page is already the network-current build. Nothing to swap.
  if (!navigator.serviceWorker.controller) {
    log('installed-ignored', { reason: 'no-controller' });
    return;
  }
  if (registration?.waiting !== worker) {
    return;
  }
  log('waiting', { visibility: document.visibilityState });
  waitingWorker = worker;
  maybeApply();
}

function maybeApply() {
  if (!waitingWorker || applying || holds > 0) {
    return;
  }
  const hidden = document.visibilityState === 'hidden';
  const fresh = Date.now() < freshUntil;
  if (!hidden && !fresh) {
    // Mid-session on a page the user is looking at: leave it waiting. The
    // next hide, resume, or composer close re-evaluates.
    return;
  }
  applying = true;
  log('apply', { hidden, fresh, holds });
  waitingWorker.postMessage({ type: 'SKIP_WAITING' });
  window.setTimeout(() => {
    // No controllerchange: activation is pinned behind in-flight events. Drop
    // the flag so the next trigger re-sends SKIP_WAITING (harmless to repeat).
    applying = false;
  }, TAKEOVER_TIMEOUT_MS);
}

function requestReload() {
  if (holds > 0) {
    reloadPending = true;
    return;
  }
  reloadPending = false;
  log('reload');
  window.location.reload();
}

function onVisibilityChange() {
  if (document.visibilityState === 'hidden') {
    maybeApply();
    return;
  }
  // Became visible: treat it like a launch.
  log('resume', { waiting: Boolean(waitingWorker), reloadPending });
  freshUntil = Date.now() + FRESH_WINDOW_MS;
  if (reloadPending) {
    requestReload();
    return;
  }
  void checkForUpdate();
  maybeApply();
}

async function checkForUpdate() {
  if (!registration) {
    return;
  }
  if (Date.now() - lastCheckAt < CHECK_MIN_GAP_MS) {
    return;
  }
  lastCheckAt = Date.now();
  try {
    await registration.update();
  } catch {
    // Offline or server unreachable — the next trigger tries again.
  }
}
